import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { MetricCard, AlertChip, TicketStatusBadge, PriorityBadge } from "../components/ui/index.jsx";
import Topbar from "../components/layout/Topbar.jsx";
import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

// ─────────────────────────────────────────────
//  COULEURS
// ─────────────────────────────────────────────

const STATUS_COLORS = {
  OPEN:        "#00C2CB",
  IN_PROGRESS: "#7C3AED",
  PENDING:     "#F59E0B",
  RESOLVED:    "#10B981",
  CLOSED:      "#6B7280",
};

const STATUS_LABELS = {
  OPEN:        "Ouverts",
  IN_PROGRESS: "En cours",
  PENDING:     "En attente",
  RESOLVED:    "Résolus",
  CLOSED:      "Fermés",
};

// ─────────────────────────────────────────────
//  TOOLTIP GRAPHIQUE BARRE
// ─────────────────────────────────────────────

function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--navy-mid)", border:"1px solid var(--border)", borderRadius:10, padding:"8px 12px", fontSize:12 }}>
      <div style={{ color:"var(--text-2)", marginBottom:4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color:p.color, fontWeight:600 }}>{p.name} : {p.value}</div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function AdminDashboardPage() {
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get("/dashboard/admin");
        setData(res.data?.data ?? res.data);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) return (
    <>
      <Topbar title="Tableau de" subtitle="bord"/>
      <div className="page-body" style={{ textAlign:"center", padding:80, color:"var(--text-3)" }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize:28, display:"block", marginBottom:10 }}/>
        Chargement du tableau de bord…
      </div>
    </>
  );

  if (error) return (
    <>
      <Topbar title="Tableau de" subtitle="bord"/>
      <div className="page-body">
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:16, color:"var(--danger)" }}>
          <i className="bi bi-exclamation-triangle me-2"/>{error}
        </div>
      </div>
    </>
  );

  const { metrics, statusCounts, weeklyData, recentTickets, clientStats } = data;

  // Données pie depuis les vrais statuts
  const pieData = Object.entries(statusCounts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v, color: STATUS_COLORS[k] || "#888" }));

  // Tickets critiques ouverts
  const criticalCount = metrics?.ticketsCritical ?? 0;

  return (
    <>
      <Topbar title="Tableau de" subtitle="bord"/>
      <div className="page-body">

        {/* ── Métriques ── */}
        <div className="dash-metrics-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
          <MetricCard
            icon="bi-ticket-detailed" color="teal"
            label="Tickets ouverts"
            value={metrics?.ticketsOpen ?? 0}
            sub="toutes entreprises"
            delay={.05}
          />
          <MetricCard
            icon="bi-exclamation-triangle" color="orange"
            label="Tickets critiques"
            value={criticalCount}
            trendType={criticalCount > 0 ? "down" : "up"}
            sub="ouverts en ce moment"
            delay={.1}
          />
          <MetricCard
            icon="bi-check2-circle" color="purple"
            label="Résolus ce mois"
            value={metrics?.ticketsResolvedThisMonth ?? 0}
            sub="toutes entreprises"
            delay={.15}
          />
          <MetricCard
            icon="bi-pc-display-horizontal" color="green"
            label="Assets supervisés"
            value={metrics?.totalAssets ?? 0}
            trend={`${metrics?.assetsOnlinePercent ?? 0}%`}
            trendType="up"
            sub="en ligne"
            delay={.2}
          />
        </div>

        {/* ── Graphiques ── */}
        <div className="dash-charts-grid" style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:14, marginBottom:20 }}>

          {/* Barres — volume de tickets */}
          <div className="itm-card anim-fade-up delay-5">
            <div className="card-title-row">
              <div>
                <div className="card-title">Volume de tickets</div>
                <div className="card-sub">Par semaine — 8 dernières semaines</div>
              </div>
              <span style={{ fontSize:11, padding:"4px 10px", borderRadius:20, background:"var(--accent-dim)", color:"var(--accent)", border:"1px solid rgba(0,194,203,.2)" }}>
                Tous clients
              </span>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={weeklyData || []} barSize={7} barGap={3}>
                <XAxis dataKey="label" tick={{ fill:"var(--text-3)", fontSize:10 }} axisLine={false} tickLine={false}/>
                <YAxis hide/>
                <Tooltip content={<BarTooltip/>} cursor={{ fill:"rgba(255,255,255,.03)" }}/>
                <Bar dataKey="created"  name="Créés"   fill="#00C2CB" radius={[4,4,0,0]}/>
                <Bar dataKey="resolved" name="Résolus" fill="#7C3AED" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display:"flex", gap:14, marginTop:10 }}>
              {[["#00C2CB","Créés"],["#7C3AED","Résolus"]].map(([c,l]) => (
                <div key={l} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--text-2)" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:c }}/>{l}
                </div>
              ))}
            </div>
          </div>

          {/* Pie — répartition statuts */}
          <div className="itm-card anim-fade-up" style={{ animationDelay:".3s" }}>
            <div className="card-title-row">
              <div>
                <div className="card-title">Statuts</div>
                <div className="card-sub">Répartition tickets actifs</div>
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}>
              <PieChart width={180} height={180}>
                <Pie data={pieData} cx={90} cy={90} innerRadius={55} outerRadius={78}
                  paddingAngle={3} dataKey="value" startAngle={90} endAngle={-270}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color}/>)}
                </Pie>
              </PieChart>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {pieData.map(d => (
                <div key={d.name} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:13 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, color:"var(--text-2)" }}>
                    <div style={{ width:10, height:10, borderRadius:3, background:d.color, flexShrink:0 }}/>{d.name}
                  </div>
                  <div style={{ fontFamily:"Syne,sans-serif", fontWeight:700, color:"var(--text-1)" }}>{d.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Bas de page ── */}
        <div className="dash-bottom-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

          {/* Tickets récents */}
          <div className="itm-card anim-fade-up" style={{ animationDelay:".35s" }}>
            <div className="card-title-row">
              <div>
                <div className="card-title">Tickets récents</div>
                <div className="card-sub">10 derniers — toutes entreprises</div>
              </div>
              <span
                onClick={() => navigate("/tickets")}
                style={{ fontSize:11, padding:"4px 10px", borderRadius:20, background:"var(--accent-dim)", color:"var(--accent)", border:"1px solid rgba(0,194,203,.2)", cursor:"pointer" }}>
                Voir tout →
              </span>
            </div>

            {criticalCount > 0 && (
              <AlertChip
                type="danger"
                icon="bi-exclamation-circle-fill"
                title={`${criticalCount} ticket${criticalCount > 1 ? "s" : ""} critique${criticalCount > 1 ? "s" : ""}`}
                sub="Sans réponse — priorité maximale"
              />
            )}

            <div className="table-responsive">
              <table className="itm-table">
                <thead>
                  <tr><th>Ticket</th><th>Entreprise</th><th>Priorité</th><th>Statut</th><th>Depuis</th></tr>
                </thead>
                <tbody>
                  {(recentTickets || []).length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign:"center", color:"var(--text-3)", padding:20 }}>Aucun ticket</td></tr>
                  ) : (recentTickets || []).map(t => (
                    <tr key={t.id} style={{ cursor:"pointer" }} onClick={() => navigate(`/tickets/${t.id}`)}>
                      <td>
                        <div className="cell-primary">{t.title}</div>
                        <div className="cell-sub">#{t.id?.substring(0,8)}</div>
                      </td>
                      <td style={{ fontSize:12, color:"var(--text-3)" }}>{t.company?.name || "—"}</td>
                      <td><PriorityBadge priority={t.priority}/></td>
                      <td><TicketStatusBadge status={t.status}/></td>
                      <td style={{ color:"var(--text-3)", fontSize:12 }}>{timeAgo(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fil d'activité simulé depuis les tickets récents */}
          <div className="itm-card anim-fade-up" style={{ animationDelay:".4s" }}>
            <div className="card-title-row">
              <div>
                <div className="card-title">Fil d'activité</div>
                <div className="card-sub">Derniers événements</div>
              </div>
            </div>
            {(recentTickets || []).length === 0 ? (
              <div style={{ textAlign:"center", padding:30, color:"var(--text-3)", fontSize:13 }}>Aucune activité récente</div>
            ) : (recentTickets || []).map((t, i) => (
              <div key={t.id}
                style={{ display:"flex", gap:10, padding:"10px 0", borderBottom: i < recentTickets.length - 1 ? "1px solid var(--border)" : "none", cursor:"pointer" }}
                onClick={() => navigate(`/tickets/${t.id}`)}>
                <div style={{ width:32, height:32, borderRadius:9, background:"var(--accent-dim)", color:"var(--accent)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                  <i className="bi bi-ticket-detailed"/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:"var(--text-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {t.title}
                    {t.company && <span style={{ color:"var(--text-3)" }}> · {t.company.name}</span>}
                  </div>
                  <div style={{ fontSize:11, color:"var(--text-3)", marginTop:2 }}>
                    <TicketStatusBadge status={t.status}/> {" "}{timeAgo(t.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Stats par client ── */}
        {clientStats && clientStats.length > 0 && (
          <div className="itm-card anim-fade-up" style={{ animationDelay:".45s" }}>
            <div className="card-title-row">
              <div>
                <div className="card-title">Activité par client</div>
                <div className="card-sub">Ce mois-ci</div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="itm-table">
                <thead>
                  <tr>
                    <th>Entreprise</th>
                    <th>Tickets ouverts</th>
                    <th>Résolus ce mois</th>
                    <th>Assets</th>
                    <th>Assets en ligne</th>
                    <th>Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {clientStats.map(c => {
                    const total = (c.open || 0) + (c.resolved || 0);
                    const pct   = total > 0 ? Math.round((c.open / total) * 100) : 0;
                    return (
                      <tr key={c.companyId}
                        style={{ cursor:"pointer" }}
                        onClick={() => navigate(`/clients/${c.companyId}`)}>
                        <td>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:28, height:28, borderRadius:8, background:"var(--accent-dim)", color:"var(--accent)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700 }}>
                              {c.companyName?.[0]}
                            </div>
                            <span className="cell-primary">{c.companyName}</span>
                          </div>
                        </td>
                        <td style={{ color: c.open > 0 ? "var(--accent)" : "var(--text-3)", fontWeight:500 }}>{c.open}</td>
                        <td style={{ color:"var(--success)" }}>{c.resolved}</td>
                        <td style={{ color:"var(--text-2)" }}>{c.assets}</td>
                        <td>
                          <span style={{ color: c.assetsOnline === c.assets ? "var(--success)" : "var(--warning)" }}>
                            {c.assetsOnline} / {c.assets}
                          </span>
                        </td>
                        <td style={{ minWidth:120 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <div className="itm-progress" style={{ flex:1 }}>
                              <div className="itm-progress-bar" style={{ width:`${pct}%`, background:"var(--accent)" }}/>
                            </div>
                            <span style={{ fontSize:11, color:"var(--text-3)", whiteSpace:"nowrap" }}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Grilles responsives — passent en une colonne sur mobile, sinon les
          cartes du tableau de bord débordent hors de l'écran (repéré le
          15/09/2026 sur téléphone). Fait en CSS (avec !important) plutôt
          qu'en JS car les grilles sont en style inline : la media query a
          besoin de gagner sur la spécificité de l'inline style. */}
      <style>{`
        @media (max-width: 900px) {
          .dash-charts-grid { grid-template-columns: 1fr !important; }
          .dash-bottom-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .dash-metrics-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 400px) {
          .dash-metrics-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}